import * as d3 from "d3";
import moment, { Moment } from "moment-timezone";
import { useMemo } from "react";
import useMediaQuery from "../../../lib/hooks/use-media-query";
import { Dimensions } from "./pricing-graph.types";
import { GRAPH_TIMEZONE } from "./pricing-graph.utils";

interface Props {
	dates: Moment[];
	boundedWidth: number;
	dimensions: Dimensions;
	showAxisLine?: boolean;
	showTickLine?: boolean;
	scale: d3.ScaleTime<number, number, never>;
	earliestDataDate: Moment;
}

export const XAxis = ({
	dates,
	boundedWidth,
	dimensions,
	showAxisLine = false,
	showTickLine = false,
	scale,
	earliestDataDate,
}: Props) => {
	// Maximum sm viewport
	const isMobile = useMediaQuery("(max-width: 640px)");
	const ticks = useMemo(() => {
		const leftDate = moment.tz(scale.invert(scale.range()[0]), GRAPH_TIMEZONE);
		const rightDate = moment.tz(scale.invert(scale.range()[1]), GRAPH_TIMEZONE);
		const difference = rightDate.diff(leftDate, "months");

		let interval: number = 1;
		let showYearOnly: boolean = false;
		if (difference >= 36) {
			interval = 12;
			showYearOnly = true;
		} else if (difference >= 24) {
			interval = 6;
		} else if (difference >= 12) {
			interval = 3;
		} else {
			interval = 2;
		}

		return scale
			.ticks((d3.timeMonth.every(interval) ?? 0) as number)
			.map((value) => {
				const valueInTz = moment.tz(value, GRAPH_TIMEZONE);
				const valueMinusDay = valueInTz.clone().subtract(1, "day");
				const valuePlusDay = valueInTz.clone().add(1, "day");
				let formattedValue = "";
				// Show empty string for past (before 1st data point) & future date
				if (
					valueMinusDay.isBefore(moment.tz(GRAPH_TIMEZONE)) &&
					valuePlusDay.isAfter(earliestDataDate)
				) {
					if (showYearOnly) {
						formattedValue = valueInTz.format("YYYY");
					} else {
						formattedValue = valueInTz.format("MMM YY");
					}
				}
				return {
					value,
					xOffset: scale(value),
					formattedValue: formattedValue,
				};
			});
	}, [dates, boundedWidth, isMobile]);

	const d = ["M", 0, 0, "H", dimensions.boundedWidth];

	return (
		<g transform={`translate(0, ${dimensions.boundedHeight})`}>
			{showAxisLine && (
				<path d={d.join(" ")} fill="none" stroke="currentColor" />
			)}
			{ticks.map(({ value, xOffset, formattedValue }) => (
				<g key={value.toString()} transform={`translate(${xOffset}, 0)`}>
					{showTickLine && <line y2={6} stroke="currentColor" />}
					<text
						key={value.toString()}
						fontSize="10"
						textAnchor="middle"
						transform="translate(0, 15)"
					>
						{formattedValue}
					</text>
					<line
						y2={-dimensions.boundedHeight}
						stroke="#d3d3d3"
						strokeWidth={0.5}
					/>
				</g>
			))}
		</g>
	);
};
